import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { ConfigDonneesAnalyseComponent } from './components/config-donnees-analyse/config-donnees-analyse.component';
import { ConfigIndicateurComponent } from './components/config-indicateur/config-indicateur.component';
import { CreerMotDePasseComponent } from './components/creer-mot-de-passe/creer-mot-de-passe.component';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { MesProjetsComponent } from './components/mes-projets/mes-projets.component';
import { MesTirsComponent } from './components/mes-tirs/mes-tirs.component';
import { MonProjetComponent } from './components/mon-projet/mon-projet.component';
import { MonSiteComponent } from './components/mon-site/mon-site.component';
import { OperateursComponent } from './components/operateurs/operateurs.component';
import { ReinitMotDePasseComponent } from './components/reinit-mot-de-passe/reinit-mot-de-passe.component';
import { SiteSetupComponent } from './components/site-setup/site-setup.component';
import { SiteComponent } from './components/site/site.component';
import { UserDashboardComponent } from './components/user-dashboard/user-dashboard.component';
import { AccueilComponent } from './components/keycloak/acceuil/acceuil.component';
import { ProfileComponent } from './components/keycloak/profile/profile.component';
import { AuthGuard } from './Guards/authGuard';
import { LandingRedirectGuard } from './Guards/landingRedirectGuard';
import { AreaAccessGuard } from './Guards/areaAccessGuard';
import { DxfManagementComponent } from './components/site-setup/dxf-management/dxf-management.component';
import { ZoneComponent } from './components/zone/zone.component';
import { SiteUsersComponent } from './components/site-setup/site-users/site-users.component';
import { SiteControlPointsComponent } from './components/site-control-points/site-control-points.component';
import { SiteCarteComponent } from './components/site-carte/site-carte.component';
import { SiteKpiComponent } from './components/site-kpi/site-kpi.component';
import { SismographControlPointComponent } from './components/sismograph-control-point/sismograph-control-point.component';
import { WorkflowModeleComponent } from './components/workflow-modele/workflow-modele.component';
import { RockTypeGuiComponent } from './components/site-setup/rock-type-gui/rock-type-gui.component';
import { EquipmentsComponent } from './components/equipments/equipments.component';
import { CostComponent } from './components/cost/cost.component';
// Standalone components for lazy loading
import { CustomerFeedbackComponent } from './components/survey/customer-feedback/customer-feedback.component';
import { SupplierFeedbackComponent } from './components/survey/supplier-feedback/supplier-feedback.component';
import { GeologyGuiComponent } from './components/Geology/geology-gui/geology-gui.component';
import { MineTrackerComponent } from './components/site-setup/mine-tracker/mine-tracker.component';
import { HoleToleranceComponent } from './components/site-setup/hole-tolerance/hole-tolerance.component';
import { ShiftConfigComponent } from './components/site-setup/shift-config/shift-config.component';
import { DrillPauseReasonsComponent } from './components/site-setup/drill-pause-reasons/drill-pause-reasons.component';
import { OperationsComponent } from './components/operations/operations.component';
import { NavBarComponent } from './layout/nav-bar/nav-bar.component';
import { AreaComponent } from './features/area/area.component';
import { SiteFeaturesComponent } from './components/site-setup/site-features/site-features.component';
import { ProfileFeatureComponent } from './features/profile/profile.component';
import { NewSiteComponent } from './features/site/new-site.component';
import { OurProductsComponent } from './features/our-products/our-products.component';


const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    canActivate: [AuthGuard, LandingRedirectGuard],
    data: { roles: ['explore_app_user'] },
    children: []
  },
  { path: 'create-password', component: CreerMotDePasseComponent, canActivate: [AuthGuard] },
  { path: 'reset-password', component: ReinitMotDePasseComponent, canActivate: [AuthGuard] },
  { path: 'new-profile', component: ProfileFeatureComponent, canActivate: [AuthGuard], data: { roles: ['explore_app_user'] } },
  
  // Lazy-loaded Admin module
  { 
    path: 'config',
    loadChildren: () => import('./modules/admin/admin.module').then(m => m.AdminModule),
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard]
  },
  
  // Lazy-loaded Workflow module
  { 
    path: 'workflows', 
    loadChildren: () => import('./modules/workflow/workflow.module').then(m => m.WorkflowModule),
    canActivate: [AuthGuard],
    data: { roles: ['manage_site'] }
  },
  
  // Standalone Survey routes
  { 
    path: 'survey', 
    canActivate: [AuthGuard],
    data: { roles: ['explore_app_user'] },
    children: [
      { path: 'customer/:tirId', component: CustomerFeedbackComponent },
      { path: 'supplier/:tirId', component: SupplierFeedbackComponent }
    ]
  },
  
  // Standalone Geology route
  { 
    path: 'geology', 
    component: GeologyGuiComponent,
    canActivate: [AuthGuard],
    data: { roles: ['manage_area'] }
  },
  
  // Legacy routes (redirect to new admin paths)
  { path: 'users', redirectTo: 'config/users', pathMatch: 'full' },
  { path: 'regions', redirectTo: 'config/regions', pathMatch: 'full' },
  { path: 'area-dashboard', redirectTo: 'config/area-dashboard', pathMatch: 'full' },
  
  // Lazy-loaded Region Setup (standalone component with Products, Workflow, and Geology)
  {
    path: 'region-setup',
    redirectTo: 'region-setup/0',
    pathMatch: 'full'
  },
  {
    path: 'region-setup/:regionId',
    loadComponent: () => import('./components/region-setup/region-setup.component').then(m => m.RegionSetupComponent),
    canActivate: [AuthGuard],
    data: { roles: ['manage_area'] },
    children: [
      { path: '', redirectTo: 'products', pathMatch: 'full' },
      { 
        path: 'products',
        loadChildren: () => import('./modules/products/products.module').then(m => m.ProductsModule)
      },
      {
        path: 'workflows',
        loadChildren: () => import('./modules/workflow/region-workflow.module').then(m => m.RegionWorkflowModule)
      },
      {
        path: 'geology',
        loadComponent: () => import('./components/Geology/geology-gui/geology-gui.component').then(m => m.GeologyGuiComponent)
      }
    ]
  },
  // Setup Organization (Product Sources)
  {
    path: 'setup-organization',
    loadComponent: () => import('./components/setup-organization/setup-organization.component').then(m => m.SetupOrganizationComponent),
    canActivate: [AuthGuard],
    data: { roles: ['manage_site'] }
  },
  { path: 'user-dashboard', component: UserDashboardComponent, canActivate: [AuthGuard], data: { roles: ['explore_app_user'] } },

  { path: 'sites', component: SiteComponent, canActivate: [AuthGuard], data: { roles: ['manage_site'] }  },
  { path: 'new-site', component: NewSiteComponent, canActivate: [AuthGuard], data: { roles: ['manage_site'] } },
  { path: 'our-products', component: OurProductsComponent, canActivate: [AuthGuard], data: { roles: ['explore_app_user'] } },
  { path: 'products', component: OurProductsComponent, canActivate: [AuthGuard], data: { roles: ['explore_app_user'] } },
  { 
    path: 'site-setup/:siteId', 
    component: SiteSetupComponent,
    canActivate: [AuthGuard], 
    canActivateChild: [AuthGuard], 
    data: { roles: ['manage_site'] },
    children: [
      { path: '', redirectTo: 'zones', pathMatch: 'full' },
      { path: 'zones', component: ZoneComponent },
      { path: 'operators', component: OperateursComponent },
      { path: 'users', component: SiteUsersComponent },
      { path: 'control-points', component: SiteControlPointsComponent },
      { path: 'hierarchy', component: SiteCarteComponent },
      { path: 'kpi', component: SiteKpiComponent },
      { path: 'sismograph', component: SismographControlPointComponent },
      { path: 'workflows', component: WorkflowModeleComponent },
      { path: 'analysis-config', component: ConfigDonneesAnalyseComponent },
      { path: 'rock-types', component: RockTypeGuiComponent },
      { path: 'equipments', component: EquipmentsComponent },
      { path: 'dxf', component: DxfManagementComponent },
      { path: 'cost', component: CostComponent },
      { path: 'mine-tracker', component: MineTrackerComponent },
      { path: 'hole-tolerance', component: HoleToleranceComponent },
      { path: 'shifts', component: ShiftConfigComponent },
      { path: 'drill-pause-reasons', component: DrillPauseReasonsComponent },
      { path: 'features', component: SiteFeaturesComponent }
    ]
  },
  { path: 'operators', component: OperateursComponent, canActivate: [AuthGuard], data: { roles: ['manage_site'] } },
  {
    path: 'mysite/:id',
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    data: { roles: ['explore_app_user'] },
    children: [
      { path: '', pathMatch: 'full', component: MonSiteComponent },
      { path: 'myprojects/:projectId', component: MonProjetComponent },
      { path: 'myprojects', component: MesProjetsComponent },
      { path: 'myblasts', component: MesTirsComponent },
      {
        path: 'operations',
        component: OperationsComponent,
        children: [
          { path: '', redirectTo: 'drill-activity-chart', pathMatch: 'full' },
          {
            path: 'drill-activity-chart',
            loadComponent: () => import('./components/operations/drill-activity-page/drill-activity-page.component').then(m => m.DrillActivityPageComponent)
          },
          {
            path: 'drill-activity-chart/:mode/:date',
            loadComponent: () => import('./components/operations/drill-activity-page/drill-activity-page.component').then(m => m.DrillActivityPageComponent)
          },
          {
            path: 'delivery-activity-chart',
            loadComponent: () => import('./components/operations/delivery-activity-page/delivery-activity-page.component').then(m => m.DeliveryActivityPageComponent)
          },
          {
            path: 'delivery-activity-chart/:mode/:date',
            loadComponent: () => import('./components/operations/delivery-activity-page/delivery-activity-page.component').then(m => m.DeliveryActivityPageComponent)
          }
        ]
      },
      { path: 'indicators', component: ConfigIndicateurComponent },
      { path: 'dashboard', component: DashboardComponent }
    ]
  },
  { path: 'config-analysis', component: ConfigDonneesAnalyseComponent, canActivate: [AuthGuard], data: { roles: ['manage_site'] } },
  { path: 'accueil', component: AccueilComponent },
  { path: 'profile', component: ProfileComponent, canActivate: [AuthGuard] },
  { path: 'area', component: AreaComponent, canActivate: [AuthGuard, AreaAccessGuard], data: { roles: ['explore_app_user'] } },
  // { path: 'new-site/:siteId', component: NewSiteComponent, canActivate: [AuthGuard], data: { roles: ['explore_app_user'] } },
  { path: '**', redirectTo: 'accueil' }
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule { }
